import React, { useState, useCallback } from 'react';
import { Upload, X, Image as ImageIcon, Crop } from 'lucide-react';
import { uploadImageToStorage } from '../utils/firebaseStorage';
import { toast } from 'sonner';
import Cropper from 'react-easy-crop';
import 'react-easy-crop/react-easy-crop.css';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';

const createImage = (url) =>
  new Promise((resolve, reject) => {
    const image = new Image();
    image.addEventListener('load', () => resolve(image));
    image.addEventListener('error', (error) => reject(error));
    image.setAttribute('crossOrigin', 'anonymous');
    image.src = url;
  });

const getCroppedImgCanvas = async (imageSrc, pixelCrop) => {
  const image = await createImage(imageSrc);
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');

  // Redimensionar exactamente a 584x584
  canvas.width = 584;
  canvas.height = 584;

  ctx.drawImage(
    image,
    pixelCrop.x,
    pixelCrop.y,
    pixelCrop.width,
    pixelCrop.height,
    0,
    0,
    584,
    584
  );

  return canvas;
};

export const ImageUploader = ({ onImageUploaded, currentImage = null, applyWatermark = false, id = "image-upload-input" }) => {
  const [uploading, setUploading] = useState(false);
  const [preview, setPreview] = useState(currentImage);
  const [file, setFile] = useState(null);

  // Crop states
  const [imageSrc, setImageSrc] = useState(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const [isCropModalOpen, setIsCropModalOpen] = useState(false);

  const onCropComplete = useCallback((croppedArea, croppedAreaPixels) => {
    setCroppedAreaPixels(croppedAreaPixels);
  }, []);

  const handleFileChange = async (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      if (!selectedFile.type.startsWith('image/')) {
        toast.error('Por favor selecciona un archivo de imagen');
        return;
      }
      
      const reader = new FileReader();
      reader.onload = () => {
        setImageSrc(reader.result);
        setIsCropModalOpen(true);
        // Reset defaults
        setCrop({ x: 0, y: 0 });
        setZoom(1);
      };
      reader.readAsDataURL(selectedFile);
    }
    // Limpiar input para permitir seleccionar la misma imagen de nuevo
    e.target.value = null;
  };

  const processCroppedImage = async () => {
    try {
      const canvas = await getCroppedImgCanvas(imageSrc, croppedAreaPixels);

      if (applyWatermark) {
        toast.info('Aplicando marca de agua...');
        const ctx = canvas.getContext('2d');
        const watermark = new Image();
        
        await new Promise((resolve, reject) => {
          watermark.onload = resolve;
          watermark.onerror = () => reject(new Error('Failed to load watermark'));
          watermark.src = '/logo.png';
        });

        const wmWidth = canvas.width * 0.25; // 25% of image width
        const wmHeight = (watermark.height / watermark.width) * wmWidth;
        const padding = canvas.width * 0.03; // 3% padding
        
        ctx.globalAlpha = 0.85; // slight transparency
        ctx.drawImage(watermark, canvas.width - wmWidth - padding, padding, wmWidth, wmHeight);
        ctx.globalAlpha = 1.0;
      }

      canvas.toBlob((blob) => {
        if (!blob) {
          toast.error('Error al generar la imagen');
          return;
        }
        const newFile = new File([blob], 'product-image.webp', {
          type: 'image/webp',
          lastModified: Date.now(),
        });
        setFile(newFile);
        setPreview(canvas.toDataURL('image/webp', 0.8));
        setIsCropModalOpen(false);
      }, 'image/webp', 0.8);

    } catch (e) {
      console.error(e);
      toast.error('Error al procesar la imagen');
    }
  };

  const handleUpload = async () => {
    if (!file) {
      toast.error('Selecciona una imagen primero');
      return;
    }

    setUploading(true);
    try {
      const imageUrl = await uploadImageToStorage(file, 'products');
      onImageUploaded(imageUrl);
      toast.success('Imagen subida correctamente');
    } catch (error) {
      console.error('Error uploading image:', error);
      toast.error('Error al subir la imagen. Verifica la configuración de Firebase Storage.');
    } finally {
      setUploading(false);
    }
  };

  const handleRemove = () => {
    setFile(null);
    setPreview(null);
    onImageUploaded('');
  };

  return (
    <div className="space-y-3">
      {preview ? (
        <div className="relative">
          <img
            src={preview}
            alt="Preview"
            className="w-full h-48 object-cover rounded-lg border border-slate-200"
          />
          <button
            type="button"
            onClick={handleRemove}
            className="absolute top-2 right-2 p-2 bg-red-500 text-white rounded-full hover:bg-red-600 transition-all"
            data-testid="remove-image-button"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      ) : (
        <div className="border-2 border-dashed border-slate-300 rounded-lg p-8 text-center hover:border-[#00C2CB] transition-all relative overflow-hidden">
          <ImageIcon className="w-12 h-12 mx-auto mb-3 text-slate-400" />
          <p className="text-sm text-[#4A4A6A] font-quicksand mb-2">
            Arrastra una imagen o haz clic para seleccionar
          </p>
          <p className="text-xs text-[#9CA3AF] font-quicksand">
            Se recortará y redimensionará a 584x584 (webp)
          </p>
        </div>
      )}

      <input
        type="file"
        accept="image/*"
        onChange={handleFileChange}
        className="hidden"
        id={id}
        data-testid={id}
      />

      <div className="flex gap-2">
        <label
          htmlFor={id}
          className="flex-1 cursor-pointer inline-flex items-center justify-center gap-2 px-4 py-2 bg-slate-100 text-[#1A1A2E] rounded-lg hover:bg-slate-200 transition-all font-quicksand font-semibold"
        >
          <Upload className="w-4 h-4" />
          {preview ? 'Cambiar Imagen' : 'Seleccionar Imagen'}
        </label>
        {file && !currentImage && (
          <button
            type="button"
            onClick={handleUpload}
            disabled={uploading}
            className="flex-1 px-4 py-2 bg-[#00C2CB] text-white rounded-lg hover:bg-[#00C2CB]/90 transition-all font-quicksand font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
            data-testid="upload-image-button"
          >
            {uploading ? 'Subiendo...' : 'Subir a Firebase'}
          </button>
        )}
      </div>

      {/* Modal de recorte */}
      <Dialog open={isCropModalOpen} onOpenChange={setIsCropModalOpen}>
        <DialogContent className="max-w-3xl h-[80vh] flex flex-col">
          <DialogHeader>
            <DialogTitle className="font-fredoka text-xl">Recortar Imagen</DialogTitle>
          </DialogHeader>
          <div className="relative flex-1 bg-slate-900 rounded-lg overflow-hidden my-4 min-h-[400px]">
            {imageSrc && (
              <Cropper
                image={imageSrc}
                crop={crop}
                zoom={zoom}
                aspect={1}
                onCropChange={setCrop}
                onCropComplete={onCropComplete}
                onZoomChange={setZoom}
              />
            )}
          </div>
          <DialogFooter className="flex-shrink-0">
            <button
              onClick={() => setIsCropModalOpen(false)}
              className="px-4 py-2 border border-slate-300 text-slate-700 rounded-lg font-quicksand font-semibold hover:bg-slate-50 mr-2"
            >
              Cancelar
            </button>
            <button
              onClick={processCroppedImage}
              className="px-4 py-2 bg-[#00C2CB] text-white rounded-lg font-quicksand font-semibold hover:bg-[#00C2CB]/90 flex items-center gap-2"
            >
              <Crop className="w-4 h-4" />
              Aplicar Recorte
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};